/**
 * @fileoverview Component for displaying a table of staff members.
 * This component includes a header with columns and a body with staff data.
 */

import { ChevronDownIcon } from '@heroicons/react/20/solid'
import { DateTime } from 'luxon'
import { useNavigate } from 'react-router-dom'
import { Tooltip } from 'react-tooltip'
import PersonIcon from '../../assets/person.svg'
import QuestionIcon from '../../assets/question.svg'
import { formatOutput } from '../../utils/formatOutput'
import { StaffStatusDropdown } from './StaffStatusDropdown'

/**
 * @function formatDate
 * @description Formats a timestamp to a relative date.
 * @param {string} timestamp - The timestamp to format.
 * @returns {JSX.Element} - A React component that renders the formatted date.
 */
function formatDate(timestamp?: string): JSX.Element {
    if (!timestamp) {
        return formatOutput(undefined)
    }
    const dt = DateTime.fromISO(timestamp)
    return <span>{dt.toRelative()}</span>
}

const hint = `Setting a staff account to 'Inactive' will block Dashboard access. This action does not affect the data associated with the account, and the account can be reactivated at any time.`
const columns = ['Staff', 'Invited', 'Last active', { title: 'Account status', hint }]

/**
 * @interface StaffListTableProps
 * @description Props for the StaffListTable component.
 * @property {Novoic.API.Staff[]} data - The data to display in the table.
 * @property {(e: unknown, row: string) => void} onRowClicked - The function to call when a row is clicked.
 */
interface StaffListTableProps {
    data: Novoic.API.Staff[]
    onRowClicked: (e: unknown, row: string) => void
}

/**
 * @function StaffListTable
 * @description Component for displaying a table of staff members.
 * @param {StaffListTableProps} props - The props for the component.
 * @returns {JSX.Element} - A React component that renders the table of staff members.
 */
export function StaffListTable({ data }: StaffListTableProps) {
    const navigate = useNavigate()
    return (
        <table className="w-full">
            <thead className="nv-thead top-[161px] sticky z-20">
                <tr className="h-12">
                    {columns.map((col, index) => {
                        const title = typeof col === 'string' ? col : col.title
                        const hint = typeof col === 'string' ? undefined : col.hint
                        return (
                            <th
                                key={title}
                                scope="col"
                                className={`${
                                    index === 0 ? 'pl-8 pr-2.5' : 'px-2.5'
                                } text-left text-sm font-medium text-gray-900 whitespace-nowrap`}
                            >
                                <button className="group inline-flex gap-x-1">
                                    {title}
                                    {hint && (
                                        <div id={`tooltip-${index}`}>
                                            <QuestionIcon className="w-5 h-5 fill-gray-400 transition-colors cursor-pointer hover:fill-indigo-500" />
                                            <Tooltip anchorSelect={`#tooltip-${index}`}>
                                                <p className="w-40 whitespace-normal text-xs font-normal">
                                                    {hint}
                                                </p>
                                            </Tooltip>
                                        </div>
                                    )}
                                    <span className="invisible ml-2 flex-none rounded text-gray-400 group-hover:visible group-focus:visible">
                                        <ChevronDownIcon className="h-5 w-5" aria-hidden="true" />
                                    </span>
                                </button>
                            </th>
                        )
                    })}
                </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 bg-white">
                {data.map((staff: Novoic.API.Staff) => (
                    <tr key={staff.id} className="hover:bg-[#f7f9ff] transition-all">
                        <td
                            className="pl-8 flex items-center gap-x-2 whitespace-nowrap pr-10 py-4 text-sm"
                            role="presentation"
                        >
                            <button
                                onClick={() => navigate(staff.id)}
                                className="flex text-left gap-x-3"
                            >
                                <div className="flex items-center justify-center w-8 h-8 bg-indigo-100 rounded-full">
                                    <PersonIcon className="fill-indigo-500 stroke-indigo-50" />
                                </div>
                                <div>
                                    <p className="font-medium">{formatOutput(staff.name)}</p>
                                    <p className="text-gray-600 text-xs font-normal">
                                        {formatOutput(staff.email)}
                                    </p>
                                </div>
                            </button>
                        </td>
                        <td className="px-2.5 whitespace-nowrap pr-10 py-4 text-sm text-gray-600">
                            <p>{formatDate(staff.created_at)}</p>
                        </td>
                        <td className="px-2.5 whitespace-nowrap pr-10 py-4 text-sm text-gray-600">
                            {formatDate(staff.last_activity_at)}
                        </td>
                        <td className="px-2.5 whitespace-nowrap pr-10 py-4 text-sm text-gray-600">
                            <StaffStatusDropdown staff={staff} />
                        </td>
                    </tr>
                ))}
            </tbody>
        </table>
    )
}
